import { asyncOperation, clientRepo, useCollectionBind, useDocumentGet, useUser } from "@zickko/client";
import { EmailKey, firestorePaths, InboundEmail } from "@zickko/shared";
import { useOrg } from "../providers/OrgProvider";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { InboundEmailListWidget } from "../widgets/email/InboundEmailListWidget";
import { InboundEmailViewWidget } from "../widgets/email/InboundEmailViewWidget";
import { ArchiveX, Clock3, CornerUpLeft, CornerUpRight, EllipsisVerticalIcon, ReplyAll, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { TrashEmail } from "@zickko/shared/src/models/TrashEmail";

export const ArchivePage = () => {
  const { org } = useOrg();
  const { user } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedEmail, setSelectedEmail] = useState<InboundEmail>();

  const emailId = searchParams.get("emailId");

  // Fetch the archived emails
  const { list: emailList = [] } = useCollectionBind(firestorePaths.archive, {
    key: {
      orgId: org.id,
      uid: user.uid,
    },
    orderByField: "createdIsoDateTimeUtc",
    orderByDirection: "desc",
    limit: 10,
  });

  useEffect(() => {
    if (!selectedEmail && emailList.length > 0) {
      const firstEmail = emailList[0];
      setSearchParams({ emailId: firstEmail.id });
      setSelectedEmail(firstEmail);
    } else if (selectedEmail) {
      setSearchParams({ emailId: selectedEmail.id });
    }
  }, [selectedEmail, emailList, setSearchParams, setSelectedEmail]);

  if (emailList === undefined) {
    return <div>Loading archived emails...</div>;
  }

  const [filterUnreadEmails, setFilterUnreadEmails] = useState(false);

  const filteredEmails = filterUnreadEmails ? emailList.filter((email) => !email.isRead) : emailList;

  const moveToInbox = async (emailKey: EmailKey, email: InboundEmail) => {
    await clientRepo.inboundEmail.set(emailKey, email);
    await clientRepo.archiveEmail.remove(emailKey);
  };

  const unarchiveEmail = async () => {
    asyncOperation({
      fn: async () => {
        const emailKey: EmailKey = {
          emailId,
          orgId: selectedEmail.orgId,
          uid: selectedEmail.uid,
        };
        await moveToInbox(emailKey, selectedEmail);
      },
      onSuccess: () => {
        toast.success("Email moved to Inbox.");
      },
      onError(e) {
        toast.error("Error moving email to Inbox.");
      },
    });
  };
  const onDeleteEmail = () => {
    asyncOperation({
      fn: async () => {
        const emailKey: EmailKey = { emailId: emailId, orgId: selectedEmail.orgId, uid: selectedEmail.uid };
        const trashEmail: TrashEmail = {
          emailId: emailId,
          orgId: selectedEmail.orgId,
          uid: selectedEmail.uid,
          email: selectedEmail,
          deletedIsoDateTimeUtc: new Date().toISOString(),
          type: "InboundEmail",
        };
        await clientRepo.trashEmail.set(emailKey, trashEmail);
        await clientRepo.archiveEmail.remove(emailKey);
      },
      onSuccess: () => {
        toast.success("Email Deleted Successfully");
      },
      onError(e) {
        toast.error("Error in deleting email.");
      },
    });
  };
  return (
    <div className="inbox-page">
      <div className="email-list-widget">
        <div className="email-list-header">
          <div className="inbox-heading">Archive</div>
          <div className="switch-button">
            <div
              className={`all-mail ${!filterUnreadEmails ? "active" : ""}`}
              onClick={() => setFilterUnreadEmails(false)}
            >
              All Mail
            </div>
          </div>
        </div>
        <InboundEmailListWidget
          emailList={emailList}
          onSelectEmail={(email) => setSelectedEmail(email)}
          filteredEmails={filteredEmails}
        />
      </div>
      <div className="email-view-widget">
        <div className="email-action-items">
          <div className="actions">
            <ArchiveX onClick={unarchiveEmail} size={18} />
            <Trash2 onClick={onDeleteEmail} size={18} />
            <div className="vertical-line"> </div>
            <Clock3 size={18} />
          </div>
          <div className="reply-actions">
            <CornerUpLeft size={18} />
            <ReplyAll size={18} />
            <CornerUpRight size={18} />
            <div className="vertical-line"> </div>
            <EllipsisVerticalIcon size={18} />
          </div>
        </div>
        <InboundEmailViewWidget email={selectedEmail} />
      </div>
    </div>
  );
};
